<template>
    <div
        v-show="activeTab === tab.cc"
        :key="tab.cc"
        id="panel-cards"
        role="tabpanel"
        aria-labelledby="tab-cards"
        :class="{ 'payment-layout__tab-content__cc': true }">
        <div v-if="formErrorMessage" role="alert" class="form-message error">
            <p v-if="formErrorMessage">
                {{ formErrorMessage }}
            </p>
        </div>
        <!-- START AB TEST PLOCTOPLUS-2033 : Added @recurlyFocus -->
        <FormFields
            v-show="activeTab === tab.cc"
            :key="tab.cc"
            class="form-cc-payment"
            v-bind:class="[
                registrationCountry ? countryFormClass : '',
                { long: altPaymentFormView },
            ]"
            :formFieldList="formFieldList"
            :fieldErrors="fieldErrors"
            :isFTC="isFTC"
            @updateFormData="updateFormData"
            @validateField="validateField"
            @validateForm="validateForm"
            @recurlyFocus="handleRecurlyFocus"></FormFields>
        <!-- END AB TEST PLOCTOPLUS-2033 : Added @recurlyFocus -->
    </div>
</template>

<script>
    import ButtonCta from 'atoms/ButtonCta';
    import FormButton from 'atoms/FormButton';
    import FormFields from 'atoms/FormFields';
    import CreditCardLogos from 'aa/vue/components/CreditCardLogos';
    import { CreditCardPaymentConfig, TAB } from 'aa/vue/FormConfig';
    // START TEST PLOCTOPLUS-1915: center align payment page
    import CenterAlignPaymentPageAbTest from 'aa/vue/abTestRelated/CenterAlignPaymentPageAbTest';
    // END TEST PLOCTOPLUS-1915: center align payment page
    import { Tooltip, TooltipConfig } from 'components/Tooltip';
    import FormState from 'aa/vue/plugins/FormState';
    import checkoutMixin from 'aa/vue/plugins/checkoutMixin';
    import commonMixins from 'aa/vue/plugins/account/commonMixins';
    import couponsMixin from 'aa/vue/plugins/couponsMixin';
    import { tracking } from 'services/Tracking';
    import { mapGetters, mapMutations } from 'vuex';
    import { RECAPTCHA_FORMS } from 'recaptcha/Recaptcha';

    export default {
        name: 'PaymentCreditCardTab',
        mixins: [
            CenterAlignPaymentPageAbTest,
            FormState,
            couponsMixin,
            commonMixins,
            checkoutMixin,
        ],
        components: {
            ButtonCta,
            FormButton,
            FormFields,
            CreditCardLogos,
        },
        props: {
            activeTab: {
                type: String,
                default: TAB.cc,
            },
            isEditPayment: {
                type: Boolean,
                default: false,
            },
            isFromPaymentSummary: {
                type: Boolean,
                default: false,
            },
            formReference: {
                type: [HTMLFormElement, null],
                default: null,
            },
            handleProcessedPaymentError: {
                type: Function,
                default: () => {},
            },
            handleProcessedPayment: {
                type: Function,
                default: () => {},
            },
            isFTC: {
                type: Boolean,
                default: false,
            },
            errorMessageElementId: {
                type: String,
                default: null,
            },
        },
        data: function () {
            return {
                enhancedPaymentSummaryEnabled: false,
                featurePaypal: false,
                acceptedCards: [],
                countryFormClass: '',
                altPaymentFormView: false,
                payPalToken: null,
                // START TEST PLOCTOPLUS-2033
                tooltip: null,
                // END TEST PLOCTOPLUS-2033
                recaptchaAction: RECAPTCHA_FORMS.FORM_PAYMENT,
            };
        },
        created() {
            this.countryFormClass = `form-${this.registrationCountry}`;
            this.altPaymentFormView = this.$store.state.serverData.altPaymentFormView;
            this.acceptedCards = this.$store.state.serverData.paymentMethods?.acceptedCreditCards;
            this.featurePaypal = this.$store.state.serverData.paymentMethods?.paypal.enabled;
            this.featureFlagPaypal = this.$store.state.serverData?.paypalAvailableInCountry;

            let inputList = this.$store.state.serverData.paymentForm;
            let { recurlyFieldConfig } = CreditCardPaymentConfig;
            let fieldConfigs = {
                ...inputList,
                ...recurlyFieldConfig,
            };
            fieldConfigs.first_name.value = this.$store.getters['user/firstName'];
            fieldConfigs.last_name.value = this.$store.getters['user/lastName'];
            this.fieldConfigs = fieldConfigs;
            this.$store.commit('ftc/setFieldConfigs', fieldConfigs);

            this.setValidationService(
                null,
                CBS?.Registry?.user?.userRegistrationCountry ?? '',
                this.$store.state.serverData.regionLocale ?? '',
            );
        },
        mounted() {
            this.enhancedPaymentSummaryEnabled =
                this.$store.state.serverData.enhancedPaymentSummaryEnabled;

            // START AB TEST PLOCTOPLUS-2033
            if (this.$store.getters['variant/isPaymentHypertextLink']) {
                this.tooltip = new Tooltip(
                    new TooltipConfig({
                        events: ['mouseover', 'click'],
                        selectorsList: ['.cc-hyperlink a', '.cc-checkmark-lock-icon'],
                        targetSelector: '.cc-checkmark-lock-icon',
                        sameWidthAsSelector: {
                            mobilePortrait: '.field-wrapper--cc-number',
                            mobileLandscape: '.field-wrapper--cc-number',
                            tabletPortrait: '.field-wrapper--cc-number',
                        },
                        innerHtmlTargetSelector: {
                            mobilePortrait: '.field-wrapper--cc-number',
                            mobileLandscape: '.field-wrapper--cc-number',
                            tabletPortrait: '.field-wrapper--cc-number',
                        },
                        innerHTML:
                            'We use SSL to encrypt your payment information while your payment is being processed. Paramount+ does not store your credit card information.',
                        trackOnShow: (event) => {
                            if (event && event.target) {
                                if (
                                    event.type === 'mouseover' &&
                                    event.target.id === 'lock-icon-hyperlink'
                                ) {
                                    this.trackingService.trackAction(
                                        'trackPaymentLearnMoreButtonHover',
                                    );
                                } else if (
                                    event.type === 'mouseover' &&
                                    event.target.id === 'cc-lock-icon'
                                ) {
                                    this.trackingService.trackAction('trackPaymentLockIconHover');
                                }
                            }
                        },
                    }),
                );
            }
            // END AB TEST PLOCTOPLUS-2033
        },
        computed: {
            ...mapGetters('ftc', ['getFieldErrors']),
            formConfig() {
                return CreditCardPaymentConfig;
            },
            tab() {
                return TAB;
            },
            trackingService() {
                return this.$trackingService ?? tracking;
            },
        },
        methods: {
            // START TEST PLOCTOPLUS-2033
            handleRecurlyFocus() {
                if (this.tooltip) {
                    this.tooltip.hide(new Event('click'));
                }
            },
            // END TEST PLOCTOPLUS-2033
        },
        watch: {
            getFieldErrors(fieldErrors) {
                this.fieldErrors = fieldErrors;
            },
        },
    };
</script>
